import { useState, useCallback } from "react";
import { useDisclosure } from "hooks/use-disclosure";
import { useNavigate } from "react-router-dom";

import { SubmitFormProps } from "components/modals/version-edit-modal/version-edit-modal";
import toast from "components/partials/toast/toast";

import { useCurrentCampaign } from "hooks/api/use-campaigns";
import { useCurrentClient } from "hooks/api/use-clients";
import { useCurrentTouchpoint } from "hooks/api/use-current-touchpoint";
import {
  useEditTouchpointVersion,
  useCreateDraftVersion,
  useInvalidateTouchpointVersions,
  usePatchTouchpointVersionStatus,
  useTouchpointVersions,
  type TouchpointVersionAttributes,
} from "hooks/api/use-touchpoint-versions";

import { getActionTouchpointTypeLabel, getDisplayTouchpointType, getErrorMessage, getTouchpointType } from "utilities";
import { TouchpointStatuses } from "types";
import { useBuilderAccess } from "containers/admin/clients/touchpoint/components/builder/use-builder-access";
import { useTouchpointLocking } from "contexts/touchpoint-locking-context";
import { getTouchpointPaths } from "utilities/app-routes";

const useTouchpointVersionsTable = (
  setPreviewSelectedVersion?: (version: TouchpointVersionAttributes) => void,
) => {
  const { accessBuilder } = useBuilderAccess();
  const navigate = useNavigate();

  const newDraftModal = useDisclosure();
  const editVersionModal = useDisclosure();
  const confirmArchiveModal = useDisclosure();
  const cloneTouchpointModal = useDisclosure();
  const confirmPublishModal = useDisclosure();

  const currentClient = useCurrentClient();
  const currentCampaign = useCurrentCampaign();
  const currentTouchpoint = useCurrentTouchpoint();
  const { setSelectedVersion: setLockingContextVersion } = useTouchpointLocking();

  const versionScope = {
    clientId: currentClient.id,
    campaignId: currentCampaign.id,
    touchpointId: currentTouchpoint.id,
  };
  const { data: touchpointVersions } = useTouchpointVersions(versionScope);
  const invalidateTouchpointVersions = useInvalidateTouchpointVersions(versionScope);
  const editVersionMutation = useEditTouchpointVersion(versionScope);
  const createDraftMutation = useCreateDraftVersion(versionScope);
  const patchStatusMutation = usePatchTouchpointVersionStatus(versionScope);

  const [isCloneTemplateOpen, setIsCloneTemplateOpen] = useState(false);
  const [isCreateParentTouchpointModalOpen, setIsCreateParentTouchpointModalOpen] = useState(false);
  const [isUpdateClientThemeModalOpen, setIsUpdateClientThemeModalOpen] = useState(false);

  const openTouchpointPreview = useCallback(
    (id: string) => {
      const version = touchpointVersions?.items.find((v) => v.id === id);
      if (version) {
        if (setPreviewSelectedVersion) {
          setPreviewSelectedVersion(version);
        }

        const { touchpointPreviewPath } = getTouchpointPaths({
          clientId: currentClient.id,
          campaignId: currentCampaign.id,
          touchpointId: currentTouchpoint.id,
        });
        navigate(touchpointPreviewPath);
      }
    },
    [
      touchpointVersions?.items,
      currentClient.id,
      currentCampaign.id,
      currentTouchpoint.id,
      navigate,
      setPreviewSelectedVersion,
    ],
  );

  const openTouchpointEdit = useCallback(
    (touchpoint: TouchpointVersionAttributes) => {
      setLockingContextVersion(touchpoint);
      accessBuilder(touchpoint);
    },
    [setLockingContextVersion, accessBuilder],
  );

  const handleSubmitEditVersion = ({ id, description, fiscalYear }: SubmitFormProps) => {
    if (id) {
      return editVersionMutation
        .mutateAsync({
          id,
          attributes: { description, fiscalYear } as Record<string, unknown>,
        })
        .then(() => {
          toast.success({
            data: {
              title: "Updated version details",
            },
          });
          invalidateTouchpointVersions();
        })
        .catch((err) => {
          toast.error({
            data: {
              title: "Failed to edit version details",
              message: getErrorMessage(err?.response?.data),
            },
          });
        });
    }
  };

  const handleSubmitNewDraft = ({
    id,
    description,
    applyClientTheme,
    fiscalYear,
  }: SubmitFormProps) => {
    if (id) {
      return createDraftMutation
        .mutateAsync({
          id,
          data: {
            description,
            applyClientTheme: applyClientTheme!,
            fiscalYear,
          } as unknown as Parameters<typeof createDraftMutation.mutateAsync>[0]["data"],
        })
        .then(() => {
          toast.success({
            data: {
              title: "Created draft",
            },
          });
          invalidateTouchpointVersions();
        })
        .catch((err) => {
          toast.error({
            data: {
              title: "Failed to create draft",
              message: getErrorMessage(err?.response?.data),
            },
          });
        });
    }
  };

  const handlePublishVersion = (id?: string) => {
    if (id) {
      patchStatusMutation
        .mutateAsync({ id, status: TouchpointStatuses.PUBLISHED })
        .then(() => {
          toast.success({
            data: {
              title: "Published creative",
            },
          });
        })
        .catch((err) => {
          toast.error({
            data: {
              title: "Failed to publish creative",
              message: getErrorMessage(err?.response?.data),
            },
          });
        });
    }
    confirmPublishModal.onClose();
  };

  const handleArchiveVersion = (touchpoint?: TouchpointVersionAttributes) => {
    if (touchpoint) {
      const touchpointType = getTouchpointType(touchpoint);
      const formattedTouchpointType = getDisplayTouchpointType(touchpointType);

      patchStatusMutation
        .mutateAsync({ id: touchpoint.id, status: TouchpointStatuses.ARCHIVED_DRAFT })
        .then(() => {
          toast.success({
            data: {
              title: `Archived ${formattedTouchpointType} version ${touchpoint.version}`,
            },
          });
        })
        .catch((err) => {
          toast.error({
            data: {
              title: `Failed to archive ${getActionTouchpointTypeLabel(touchpointType).toLowerCase()}`,
              message: getErrorMessage(err?.response?.data),
            },
          });
        });
    }
    confirmArchiveModal.onClose();
  };

  return {
    currentClient,
    currentCampaign,
    currentTouchpoint,
    // Modals
    newDraftModal,
    editVersionModal,
    confirmArchiveModal,
    cloneTouchpointModal,
    confirmPublishModal,
    // Quick actions
    openTouchpointEdit,
    openTouchpointPreview,
    // Modals handlers
    handleSubmitEditVersion,
    handleSubmitNewDraft,
    handlePublishVersion,
    handleArchiveVersion,
    // Other for modals
    isCloneTemplateOpen,
    setIsCloneTemplateOpen,
    isCreateParentTouchpointModalOpen,
    setIsCreateParentTouchpointModalOpen,
    isUpdateClientThemeModalOpen,
    setIsUpdateClientThemeModalOpen,
  };
};

export default useTouchpointVersionsTable;
